import type { PlanDetailsCopy } from '../planDetails'
import { arPlural } from './plural'

const ar: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'عمرك',
    retirement_age: 'موعد تقاعدك',
    annual_income: 'الدخل السنوي',
    annual_savings: 'الادخار السنوي',
    inflation: 'معدل التضخم',
    realization_tax_rate: 'معدل ضريبة الأرباح الرأسمالية',
    cost_basis: 'تكلفة الاقتناء',
    w0: 'الأصول القابلة للاستثمار اليوم',
    primary_residence: 'مسكنك',
    total_investable: 'الأصول القابلة للاستثمار',
    expected_return: 'العائد المتوقع',
    volatility: 'التقلب',
    horizon: 'أفق التخطيط',
    jurisdiction: 'الإقامة الضريبية',
    risk_aversion: 'تحمّل المخاطر',
    birth_year: 'سنة الميلاد',
    birth_date: 'تاريخ الميلاد',
    marital_status: 'الحالة الاجتماعية',
    dependents: 'المُعالون',
    dependent_ages: 'أعمار المُعالين',
    children: 'الأبناء',
    children_birth_years: 'سنوات ميلاد الأبناء',
    partner_age: 'عمر الشريك',
    partner_annual_income: 'الدخل السنوي للشريك',
    partner_retirement_age: 'سن تقاعد الشريك',
    plan_to_age: 'العمر الذي تمتد إليه الخطة',
    gross_annual_income: 'الدخل السنوي الإجمالي',
    net_income: 'صافي الدخل',
    annual_spending: 'الإنفاق السنوي',
    essential_spending: 'الإنفاق الأساسي',
    flexible_spending: 'الإنفاق المرن',
    investments_total: 'إجمالي المستثمَر',
    total_investable_wealth: 'إجمالي الأصول القابلة للاستثمار',
    portfolio_mix: 'توزيع المحفظة',
    cgt_rate: 'معدل الضريبة على عوائد رأس المال',
    interest_rate: 'سعر الفائدة',
    dividend_yield: 'عائد التوزيعات',
    wealth_tax_rate: 'معدل ضريبة الثروة',
    filing_jurisdictions: 'دول تقديم الإقرار الضريبي',
    citizenship: 'الجنسية',
    tax_residency: 'الإقامة الضريبية المُصرَّح بها',
    state: 'الولاية أو المقاطعة',
    region: 'المنطقة',
    city: 'المدينة',
    locality: 'البلدية',
    church_tax_applicable: 'خاضع لضريبة الكنيسة',
    life_cover: 'تغطية التأمين على الحياة',
    income_protection: 'تأمين حماية الدخل',
    employer_match: 'مساهمة صاحب العمل',
    pension: 'المعاش التقاعدي',
    state_pension: 'المعاش الحكومي',
    social_security: 'الضمان الاجتماعي',
    wage_growth: 'نمو الأجور',
    stability: 'استقرار الدخل',
    has_will: 'وجود وصية',
    risk_attitude: 'الموقف من المخاطر',
  },
  keyLabels: {
    goal_priority: 'ترتيب الأهداف',
    current_age: 'العمر',
    inflation: 'افتراض التضخم',
    jurisdiction: 'الإقامة الضريبية',
    realization_tax_rate: 'الضريبة عند البيع لتمويل هدف',
    citizenship: 'الجنسية',
    total_investable: 'الثروة القابلة للاستثمار',
    annual_income: 'الدخل السنوي',
    annual_savings: 'الادخار السنوي',
    risk_tier: 'مستوى المخاطر',
    retirement_age: 'سن التقاعد',
    target_wealth: 'الثروة المستهدفة',
    primary_residence_sellable: 'بيع المسكن / الانتقال إلى مسكن أصغر',
  },
  channelLabels: {
    web_levers: 'أدوات الضبط على الموقع',
    web_cockpit: 'لوحة القرارات',
    web: 'الموقع الإلكتروني',
    chat: 'المساعد الذكي',
    mcp: 'المساعد الذكي',
    full_review: 'مراجعة كاملة',
    full_plan: 'خطة كاملة',
    what_if: 'تحليل السيناريوهات',
  },
  objectiveLabels: {
    status_quo: 'الوضع الحالي',
    max_goal_probability: 'أعلى احتمال نجاح',
    lowest_drawdown: 'أدنى تراجع أقصى',
    highest_growth: 'أعلى نمو',
    fastest_to_goal: 'بلوغ الهدف في أقرب وقت',
  },
  choices: {
    interest_only: 'سداد الفائدة فقط',
    fixed_rate_amortizing: 'سداد تدريجي للأصل (فائدة ثابتة)',
    variable_rate_amortizing: 'سداد تدريجي للأصل (فائدة متغيرة)',
    repayment: 'سداد تدريجي للأصل',
    refinance: 'إعادة التمويل بسعر السوق',
    repay: 'السداد الكامل',
  },
  values: {
    yearsShort: (n) => `${n} سنة`,
    shareAbout: (pct) => `نحو ${pct} من قيمته`,
    sharePart: 'جزء من قيمته',
    aroundAge: (age) => ` في سن ${age} تقريبًا`,
    inAboutYears: (n) => ` بعد نحو ${n} سنة`,
    maySellFor: (share, when) => `يمكن بيعه لتوفير ${share}${when}، فقط إذا كان ذلك يحسّن الخطة.`,
    maySell: 'يمكن بيعه إذا كان ذلك يحسّن الخطة.',
    structuredFact: 'معلومة منظَّمة',
  },
  ask: { talkThrough: 'ناقش هذا مع MyCIO' },
  review: {
    openInputCheckFailed: 'تعذّر إكمال فحص البيانات الناقصة. راجع حالة المراجعة أدناه.',
    finishedLoading: 'اكتملت مراجعتك. جارٍ تحميل النتائج…',
    draftReady: 'مراجعتك المبدئية جاهزة — تجد أدناه التحليل المكتمل، وما تعذّر حسابه، والأسئلة التي تتيح استكماله.',
    finishedNotLoaded: 'اكتملت مراجعتك، لكن تعذّر تحميل نتائجها حتى الآن. جارٍ إعادة المحاولة — لن يُعاد الحساب.',
    fetchFailed: 'اكتملت مراجعتك، لكن تعذّر جلبها الآن. إنها محفوظة — حاول مجددًا بعد قليل؛ لا حاجة إلى إعادة الحساب.',
    notComputed: (list) => `لم يُحسب في هذا التشغيل: ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? 'لم يُحفظ تغيير واحد' : n === 2 ? 'لم يُحفظ تغييران' : `لم يُحفظ ${f} ${arPlural(n, 'تغيير', 'تغييران', 'تغييرات', 'تغييرًا')}`} — راجع «معلوماتك».`,
  },
  facts: {
    chooseOption: 'اختر أحد الخيارات.',
    couldNotSave: 'تعذّر حفظ هذه القيمة. لم تتغير خطتك.',
    couldNotUndo: 'تعذّر التراجع عن هذا التغيير.',
    choosePlaceholder: 'اختر…',
    confirmedBadge: 'مؤكَّد — اطلب التغيير في المحادثة',
    confirmedTitle: (was) => `سبق أن أكّدت هذه القيمة على أنها ${was}. اطلب من MyCIO في المحادثة استبدالها.`,
  },
  assumptionsToConfirm: { anInput: 'أحد المدخلات', assumedSuffix: (v) => ` — القيمة المفترضة: ${v}` },
  editor: {
    enterNumber: 'أدخل رقمًا.',
    enterNumberFor: (l) => `أدخل رقمًا في «${l}».`,
    enterPercentFor: (l) => `أدخل نسبة مئوية في «${l}».`,
    chooseYesNoFor: (l) => `اختر نعم أو لا في «${l}».`,
    semicolonBetween: (l) => `افصل بين عناصر «${l}» بالرمز «;».`,
    useNumbersFor: (l) => `استخدم أرقامًا في «${l}».`,
    wholeNumbersFor: (l) => `استخدم أعدادًا صحيحة في «${l}» وافصل بينها بالرمز «;».`,
    commaSeparated: 'مفصولة بفواصل',
  },
  freshnessUnknown: 'تعذّر علينا التحقق مما إذا كان حسابك المحفوظ يتضمن أحدث التغييرات في حساباتك. حدّث الصفحة وراجع حساباتك قبل الاعتماد على هذه الأرقام.',
  goals: { goalN: (n) => `الهدف ${n}`, whatMoves: 'ما الذي يحرّكه ←', earliest: (y) => `في أقرب تقدير ≈ ${y}`, rank: 'الترتيب' },
  issues: {
    mainConstraint: 'القيد الرئيسي على هذه الخطة',
    riskToReview: 'خطر للمراجعة',
    missingInformation: 'معلومات ناقصة',
    whyItMatters: (t) => `لماذا يهم: ${t}`,
    seeEvidence: 'اطّلع على الأدلة الداعمة ↓',
    noneFound: 'لم يحدد التحليل المكتمل أي مسألة جوهرية بناءً على المعلومات الموجودة حاليًا في هذه الخطة.',
    notAvailable: 'تحليل المسائل غير متاح بعد. لا تعتبر خلوّ هذا القسم دليلًا على عدم وجود مسائل.',
    discussQuestion: 'راجع المسائل التي حددتها هذه الخطة، واشرح الأدلة عليها، واسألني إن كان أي جزء ناقص من ظروفي قد يغيّر أولويتها أو نتيجتها.',
    discussLabel: 'ناقش هذه المسائل',
  },
  policies: {
    optionTested: 'خيار تم اختباره',
    computedEffect: (v) => `الأثر المحسوب: ${v}`,
    tradeoff: (v) => `المفاضلة: ${v}`,
    backToAction: 'العودة إلى الإجراء ↑',
    tradeoffsHeading: 'المفاضلات في الحالات التي تم اختبارها',
  },
  transfer: {
    toHeirs: (m) => `+${m} للورثة`,
    fundedPoints: (label, pts) => `${label} ${pts} نقطة مئوية`,
    noChange: ' · لا تغيير في تمويل أهدافك',
  },
  tax: {
    rulesIntro: 'لا تُعرض إلا القواعد المرتبطة بهذا الحساب. يعرض كل بند تفاصيل المصدر كما سُجّلت؛ وتبقى التواريخ أو معلومات نطاق التطبيق الناقصة ظاهرة على أنها ناقصة.',
    title: 'المعاملة الضريبية المستخدمة في هذه الخطة',
    countryBookPrimary: 'طبّق الإسقاط القواعد الضريبية للبلد المعني سنةً بسنة. المصادر المسجلة صراحةً بوصفها رسمية أو أولية مدرجة أدناه.',
    countryBookNoPrimary: 'طبّق الإسقاط القواعد الضريبية للبلد المعني سنةً بسنة. لم تُرفق بهذه النتيجة المحفوظة روابط لمصادر أولية بعد، لذا تحقّق من القرارات ذات الأثر الضريبي قبل التنفيذ.',
    simplified: 'استخدم الحساب معاملة ضريبية مبسّطة. راجع القيود أدناه قبل الاعتماد على مقارنات ذات أثر ضريبي.',
    askQuestion: 'اشرح القوانين الضريبية والمستويات الوطنية التي طُبّقت على هذه الخطة، وما الذي قُرّب أو استُبعد منها، وما الحقائق في وضعي الضريبي التي قد تغيّر النتيجة. استخدم فقط المصادر الأولية المرفقة فعلًا بهذا الحساب، واذكر أي مصادر ناقصة.',
    askLabel: 'اسأل عن المعاملة الضريبية',
  },
  inputs: { title: 'مدخلات قد تغيّر النتيجة', infoToConfirm: 'معلومات تحتاج إلى تأكيد' },
  gated: {
    countChoices: (n, f) => (n === 1 ? 'خيار واحد أعددناه لك' : n === 2 ? 'خياران أعددناهما لك' : `${f} ${arPlural(n, 'خيار', 'خياران', 'خيارات', 'خيارًا')} أعددناها لك`),
    mappedYears: (n, f) => `رسمنا ملامح ${n === 1 ? 'السنة القادمة' : n === 2 ? 'السنتين القادمتين' : `${f} ${arPlural(n, 'سنة', 'سنتين', 'سنوات', 'سنة')} القادمة`}`,
    extraChecks: (n, f) => (n === 1 ? 'طريقة إضافية واحدة فحصنا بها خطتك' : n === 2 ? 'طريقتان إضافيتان فحصنا بهما خطتك' : `${f} ${arPlural(n, 'طريقة', 'طريقتان', 'طرق', 'طريقة')} إضافية فحصنا بها خطتك`),
    placeholderRows: ['صف توضيحي', 'رقم نموذجي', 'خيار على سبيل المثال', 'عنصر نائب'],
  },
}

export default ar
